import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Input } from "antd";
import { DownOutlined, TeamOutlined } from "@ant-design/icons";
import { Dropdown } from "../../../design-system";

import { canDo, Project, ProjectAction, ProjectCollaborator } from "../../../types/ProjectTypes";
import { User, UserRole } from "../../../types/UserGroup";
import { setCollaborator, removeCollaborator } from "../../../common/api/ProjectClient";
import { listUsers } from "../../../common/api/UserClient";
import { handleError } from "../../../common/ErrorHandling";
import { useClickOutside } from "../../../common/useClickOutside";
import styles from "./CollaboratorsWidget.module.less";

interface CollaboratorsWidgetProps {
    project: Project;
    currentUserId?: number;
    onUpdate: () => void;
}

export const CollaboratorsWidget: FC<CollaboratorsWidgetProps> = ({
    project,
    currentUserId,
    onUpdate,
}) => {
    const projectId = project.id;
    const owner = project.owner;
    const collaboratorsProp = project.collaborators;
    const canManage = canDo(project, ProjectAction.SHARE);
    const [open, setOpen] = useState(false);
    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [search, setSearch] = useState("");
    const [pendingUserIds, setPendingUserIds] = useState<Set<number>>(new Set());
    /** Per-user `canEdit` overrides applied locally between click and the parent's refetch. */
    const [optimisticCanEdit, setOptimisticCanEdit] = useState<Map<number, boolean>>(new Map());
    const dropdownRef = useRef<HTMLDivElement>(null);

    const baseCollaborators: ProjectCollaborator[] = collaboratorsProp ?? [];
    const collaborators: ProjectCollaborator[] = baseCollaborators.map((c) => {
        const override = optimisticCanEdit.get(c.user.id);
        return override === undefined ? c : { ...c, canEdit: override };
    });
    const collaboratorByUserId = new Map(collaborators.map((c) => [c.user.id, c]));

    // Drop optimistic overrides once the prop reflects them (parent has refetched).
    useEffect(() => {
        if (optimisticCanEdit.size === 0) return;
        let changed = false;
        const next = new Map(optimisticCanEdit);
        for (const c of baseCollaborators) {
            const override = next.get(c.user.id);
            if (override !== undefined && override === c.canEdit) {
                next.delete(c.user.id);
                changed = true;
            }
        }
        if (changed) setOptimisticCanEdit(next);
    }, [baseCollaborators, optimisticCanEdit]);

    // Load all users when the dropdown opens (only needed for the manage view).
    useEffect(() => {
        if (!open || !canManage) return;
        listUsers().then(setAllUsers).catch(handleError);
    }, [open, canManage]);

    useClickOutside(dropdownRef, open, () => setOpen(false));

    const markPending = (userId: number, pending: boolean) => {
        setPendingUserIds((prev) => {
            const next = new Set(prev);
            if (pending) next.add(userId);
            else next.delete(userId);
            return next;
        });
    };

    const addCollaborator = useCallback(
        async (userId: number) => {
            if (pendingUserIds.has(userId)) return;
            markPending(userId, true);
            setSearch("");
            try {
                await setCollaborator(projectId, userId, true);
                onUpdate();
            } catch (err) {
                handleError(err);
            } finally {
                markPending(userId, false);
            }
        },
        [pendingUserIds, projectId, onUpdate]
    );

    const toggleCanEdit = useCallback(
        async (userId: number, canEdit: boolean) => {
            if (pendingUserIds.has(userId)) return;
            markPending(userId, true);
            // Optimistic flip — gives instant feedback; cleared once the parent refetches.
            setOptimisticCanEdit((prev) => {
                const next = new Map(prev);
                next.set(userId, canEdit);
                return next;
            });
            try {
                await setCollaborator(projectId, userId, canEdit);
                onUpdate();
            } catch (err) {
                // Roll back the optimistic update on error.
                setOptimisticCanEdit((prev) => {
                    const next = new Map(prev);
                    next.delete(userId);
                    return next;
                });
                handleError(err);
            } finally {
                markPending(userId, false);
            }
        },
        [pendingUserIds, projectId, onUpdate]
    );

    const remove = useCallback(
        async (userId: number) => {
            if (pendingUserIds.has(userId)) return;
            markPending(userId, true);
            try {
                await removeCollaborator(projectId, userId);
                onUpdate();
            } catch (err) {
                handleError(err);
            } finally {
                markPending(userId, false);
            }
        },
        [pendingUserIds, projectId, onUpdate]
    );

    const displayName = (user: User): string =>
        [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email || user.name;

    const matchesSearch = (user: User): boolean => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
            (user.firstName || "").toLowerCase().includes(q) ||
            (user.lastName || "").toLowerCase().includes(q) ||
            (user.name || "").toLowerCase().includes(q) ||
            (user.email || "").toLowerCase().includes(q)
        );
    };

    const sortedCollaborators = [...collaborators].sort((a, b) =>
        displayName(a.user).localeCompare(displayName(b.user))
    );

    const candidates = allUsers
        .filter(
            (u) =>
                u.id !== owner?.id &&
                !collaboratorByUserId.has(u.id) &&
                !u.roles?.includes(UserRole.BOT)
        )
        .filter(matchesSearch)
        .sort((a, b) => displayName(a).localeCompare(displayName(b)));

    const collaboratorCount = collaborators.length + 1;

    return (
        <div className={styles.wrapper} ref={dropdownRef}>
            <button
                className={styles.trigger}
                onClick={() => setOpen((v) => !v)}
                title="Collaborators"
            >
                <TeamOutlined className={styles.triggerIcon} />
                {collaboratorCount > 0 && (
                    <span className={styles.count}>{collaboratorCount}</span>
                )}
            </button>

            {open && (
                <div className={styles.dropdown}>
                    <div className={styles.sectionHeader}>Collaborators</div>

                    {owner && (
                        <div className={`${styles.userRow} ${styles.userRowStatic}`}>
                            <span className={styles.userAvatar}>
                                {(displayName(owner)[0] || "?").toUpperCase()}
                            </span>
                            <span className={styles.userInfo}>
                                <span className={styles.userName}>
                                    {displayName(owner)}
                                    {owner.id === currentUserId && (
                                        <span className={styles.selfTag}> (you)</span>
                                    )}
                                </span>
                                {owner.email && (
                                    <span className={styles.userEmail}>{owner.email}</span>
                                )}
                            </span>
                            <span className={`${styles.rolePill} ${styles.rolePillStatic}`}>
                                Owner
                            </span>
                        </div>
                    )}

                    {sortedCollaborators.map((c) => {
                        const isSelf = c.user.id === currentUserId;
                        const isPending = pendingUserIds.has(c.user.id);
                        return (
                            <div
                                key={c.user.id}
                                className={`${styles.userRow} ${isPending ? styles.userRowPending : ""}`}
                            >
                                <span className={styles.userAvatar}>
                                    {(displayName(c.user)[0] || "?").toUpperCase()}
                                </span>
                                <span className={styles.userInfo}>
                                    <span className={styles.userName}>
                                        {displayName(c.user)}
                                        {isSelf && <span className={styles.selfTag}> (you)</span>}
                                    </span>
                                    {c.user.email && (
                                        <span className={styles.userEmail}>{c.user.email}</span>
                                    )}
                                </span>
                                {canManage ? (
                                    <Dropdown
                                        trigger={["click"]}
                                        disabled={isPending}
                                        getPopupContainer={() => dropdownRef.current ?? document.body}
                                        menu={{
                                            items: [
                                                { key: "viewer", label: "Viewer" },
                                                { key: "editor", label: "Editor" },
                                                { type: "divider" },
                                                { key: "remove", label: "Remove", danger: true },
                                            ],
                                            selectable: true,
                                            selectedKeys: [c.canEdit ? "editor" : "viewer"],
                                            onClick: ({ key, domEvent }) => {
                                                domEvent.stopPropagation();
                                                if (key === "remove") {
                                                    void remove(c.user.id);
                                                } else {
                                                    void toggleCanEdit(c.user.id, key === "editor");
                                                }
                                            },
                                        }}
                                    >
                                        <button
                                            type="button"
                                            className={styles.rolePill}
                                            onClick={(e) => e.stopPropagation()}
                                            disabled={isPending}
                                        >
                                            {c.canEdit ? "Editor" : "Viewer"}
                                            <DownOutlined className={styles.rolePillChevron} />
                                        </button>
                                    </Dropdown>
                                ) : (
                                    <span className={`${styles.rolePill} ${styles.rolePillStatic}`}>
                                        {c.canEdit ? "Editor" : "Viewer"}
                                    </span>
                                )}
                            </div>
                        );
                    })}

                    {canManage && (
                        <>
                            <div className={styles.divider} />

                            <div className={styles.searchWrapper}>
                                <Input
                                    size="small"
                                    placeholder="Add collaborator..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    allowClear
                                />
                            </div>

                            <div className={styles.userList}>
                                {candidates.length === 0 ? (
                                    <div className={styles.emptySection}>
                                        {search ? "No results" : "Everyone is already added."}
                                    </div>
                                ) : (
                                    candidates.map((user) => {
                                        const isPending = pendingUserIds.has(user.id);
                                        return (
                                            <button
                                                key={user.id}
                                                type="button"
                                                className={`${styles.userRow} ${styles.userRowClickable} ${isPending ? styles.userRowPending : ""}`}
                                                onClick={() => addCollaborator(user.id)}
                                                disabled={isPending}
                                            >
                                                <span className={styles.userAvatar}>
                                                    {(displayName(user)[0] || "?").toUpperCase()}
                                                </span>
                                                <span className={styles.userInfo}>
                                                    <span className={styles.userName}>
                                                        {displayName(user)}
                                                    </span>
                                                    {user.email && (
                                                        <span className={styles.userEmail}>
                                                            {user.email}
                                                        </span>
                                                    )}
                                                </span>
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
